import '@common/modules/workspaces/host'

import { WorkspaceService } from '@graft/nuget-gc.webportals.workspaces'
import type { StateCreator } from 'zustand'

import { getSessionToken } from '@common/core/helpers'

import {
  WORKSPACE_NOTIFICATION_CHANNEL,
  type WorkspaceNotificationChannel,
  type WorkspaceNotificationGroup,
  type WorkspaceNotificationGroupId,
  type WorkspaceNotificationId,
} from '../../../types'
import { WORKSPACE_NOTIFICATIONS_MOCK } from '../../const'
import type { WorkspaceStore } from '../types'

import type { WorkspaceNotificationsActionsSlice } from './workspace-notifications-actions.slice.type'

function updateNotificationChannel(
  notifications: WorkspaceNotificationGroup[],
  notificationId: WorkspaceNotificationId,
  channel: WorkspaceNotificationChannel,
  enabled: boolean
) {
  for (const group of notifications) {
    const item = group.items.find((notification) => notification.id === notificationId)
    if (item) {
      item.channels[channel] = enabled
      return
    }
  }
}

function updateGroupChannels(
  notifications: WorkspaceNotificationGroup[],
  groupId: WorkspaceNotificationGroupId,
  enabled: boolean,
  channel?: WorkspaceNotificationChannel
) {
  const group = notifications.find((item) => item.id === groupId)
  if (!group) return

  for (const notification of group.items) {
    if (channel) {
      notification.channels[channel] = enabled
    } else {
      notification.channels[WORKSPACE_NOTIFICATION_CHANNEL.EMAIL] = enabled
      notification.channels[WORKSPACE_NOTIFICATION_CHANNEL.IN_APP] = enabled
    }
  }
}

export const createWorkspaceNotificationsActionsSlice: StateCreator<
  WorkspaceStore,
  [['zustand/immer', never]],
  [],
  WorkspaceNotificationsActionsSlice
> = (set, get) => ({
  getWorkspaceNotifications: async () => {
    const workspace = get().workspace
    if (!workspace) return

    set((state: WorkspaceStore) => {
      state.loading = true
      state.error = null
    })

    try {
      const token = await getSessionToken()
      // @ts-expect-error - method pending SDK release
      const notifications = await WorkspaceService.getWorkspaceNotifications(token, workspace.id)
      set((state: WorkspaceStore) => {
        state.notifications = notifications ?? structuredClone(WORKSPACE_NOTIFICATIONS_MOCK)
      })
    } catch {
      set((state: WorkspaceStore) => {
        state.notifications = structuredClone(WORKSPACE_NOTIFICATIONS_MOCK)
      })
    } finally {
      set((state: WorkspaceStore) => {
        state.loading = false
      })
    }
  },

  updateWorkspaceNotificationChannel: async (notificationId, channel, enabled) => {
    const workspace = get().workspace
    if (!workspace) return

    set((state: WorkspaceStore) => {
      state.loading = true
      state.error = null
      updateNotificationChannel(state.notifications, notificationId, channel, enabled)
    })

    try {
      const token = await getSessionToken()
      // @ts-expect-error - method pending SDK release
      await WorkspaceService.updateWorkspaceNotificationChannel(token, workspace.id, notificationId, channel, enabled)
    } catch (err) {
      set((state: WorkspaceStore) => {
        updateNotificationChannel(state.notifications, notificationId, channel, !enabled)
        state.error = err instanceof Error ? err.message : 'Failed to update workspace notification'
      })
    } finally {
      set((state: WorkspaceStore) => {
        state.loading = false
      })
    }
  },

  updateWorkspaceNotificationGroup: async (groupId, enabled, channel) => {
    const workspace = get().workspace
    if (!workspace) return

    set((state: WorkspaceStore) => {
      state.loading = true
      state.error = null
      updateGroupChannels(state.notifications, groupId, enabled, channel)
    })

    try {
      const token = await getSessionToken()
      // @ts-expect-error - method pending SDK release
      await WorkspaceService.updateWorkspaceNotificationGroup(token, workspace.id, groupId, enabled, channel)
    } catch (err) {
      set((state: WorkspaceStore) => {
        updateGroupChannels(state.notifications, groupId, !enabled, channel)
        state.error = err instanceof Error ? err.message : 'Failed to update workspace notification group'
      })
    } finally {
      set((state: WorkspaceStore) => {
        state.loading = false
      })
    }
  },
})
